import type { ReactNode } from 'react';
import type { TechnologyInfoSectionTone } from './TechnologyInfoSectionTone';

type TechnologyInfoSectionProps = {
  title: string;
  subtitle?: string;
  tone: TechnologyInfoSectionTone;
  children: ReactNode;
};

const TONE_STYLES: Record<
  TechnologyInfoSectionTone,
  { card: string; accent: string; title: string; subtitle: string; body: string }
> = {
  sky: {
    card: 'border-sky-200/80 bg-sky-50/80',
    accent: 'border-l-sky-500',
    title: 'text-sky-900',
    subtitle: 'text-sky-700/80',
    body: 'text-sky-900/80',
  },
  emerald: {
    card: 'border-emerald-200/80 bg-emerald-50/80',
    accent: 'border-l-emerald-500',
    title: 'text-emerald-900',
    subtitle: 'text-emerald-700/80',
    body: 'text-emerald-900/80',
  },
  violet: {
    card: 'border-violet-200/80 bg-violet-50/80',
    accent: 'border-l-violet-500',
    title: 'text-violet-900',
    subtitle: 'text-violet-700/80',
    body: 'text-violet-900/80',
  },
  amber: {
    card: 'border-amber-200/80 bg-amber-50/80',
    accent: 'border-l-amber-500',
    title: 'text-amber-900',
    subtitle: 'text-amber-800/80',
    body: 'text-amber-900/80',
  },
  slate: {
    card: 'border-slate-200/80 bg-slate-50/80',
    accent: 'border-l-slate-400',
    title: 'text-slate-800',
    subtitle: 'text-slate-600',
    body: 'text-slate-600',
  },
};

export function TechnologyInfoSection({
  title,
  subtitle,
  tone,
  children,
}: TechnologyInfoSectionProps) {
  const styles = TONE_STYLES[tone];

  return (
    <section className={`rounded-xl border border-l-4 p-5 shadow-sm ${styles.card} ${styles.accent}`}>
      <div className="mb-3">
        <h2 className={`text-sm font-semibold tracking-wide ${styles.title}`}>{title}</h2>
        {subtitle ? <p className={`mt-1 text-xs ${styles.subtitle}`}>{subtitle}</p> : null}
      </div>
      <div className={`text-sm leading-relaxed ${styles.body}`}>{children}</div>
    </section>
  );
}
